import { Ref, CSSProperties } from 'react';
import { ViewStyle, Animated } from 'react-native';
import { PlayerBase, TimeInMs } from '@24i/player-base';

export enum ASSET_TYPE {
    EPG = 'epg',
    EPISODE = 'episode',
    LIVE_EVENT = 'live_event',
    TEXT_PAGE = 'textpage',
    MOVIE = 'movie',
    SERIES = 'series',
    CHANNEL = 'channel',
    BROADCAST = 'broadcast',
    HLS = 'hls',
    MP4 = 'mp4',
    DASH = 'dash',
    CLIP = 'clip',
    CUSTOM = 'custom_item',
    EXTERNAL = 'external',
    PAGE = 'page',
    PODCAST_SERIES = 'podcast',
}

export interface IProgramRange {
    start: number;
    end: number;
}

export interface IBuffered {
    playerInstance: PlayerBase;
    duration: number;
    className?: string;
    style?: CSSProperties | ViewStyle;
}

export interface IMidroll {
    fromLeft: number;
    length: number;
}

export interface IMidrolls {
    playerInstance: PlayerBase;
    className?: string;
    style?: CSSProperties | ViewStyle;
}

export interface IProgressTimeStyles {
    container?: CSSProperties | ViewStyle;
    backgroundContainer?: CSSProperties | ViewStyle;
    time?: CSSProperties | ViewStyle;
}

export interface IProgressTime {
    progress?: number;
    duration: number;
    isVisible: boolean;
    className?: string;
    playerInstance: PlayerBase;
    programRange?: IProgramRange | null;
    showHours?: boolean | 'auto';
    styles?: IProgressTimeStyles;
    overrideTime?: string | null;
    forceLiveTime: boolean;
    isSeeking: boolean;
}

export interface IThumbnailStyles {
    width?: number;
    height?: number;
    borderRadius?: number;
}

export interface IAbsoluteContainer {
    color?: string;
    progress: number;
    containerHeight?: any;
    dvrWindowFromLeft?: number;
    dvrWindowFromRight?: number;
}

interface ComponentVariant {
    variant?: 'podcast';
}
export interface IContainer extends ComponentVariant {
    isActive: boolean;
    isVolumeOpened: boolean;
}
export interface IProgressDot extends IAbsoluteContainer, IContainer {
    dotSize: number;
    containerHeight: number;
    color?: string;
    style?: CSSProperties | ViewStyle;
}

export interface IAnimatedProgressDot extends IAbsoluteContainer, IContainer {
    containerHeight: Animated.Value;
    testID?: string;
    pointerEvents?: 'auto' | 'none' | 'box-none' | 'box-only';
    className?: string;
    dotSize?: number;
    color?: string;
    styles?: ViewStyle | CSSProperties;
}

export interface IDVRWindow {
    dvrWindowFromLeft: number;
    dvrWindowFromRight: number;
    containerHeight: any;
}

export interface ITimelineStyles {
    container?: CSSProperties | ViewStyle;
    backgroundContainer?: CSSProperties | ViewStyle;
    buffered?: CSSProperties | ViewStyle;
    dvrWindow?: CSSProperties | ViewStyle;
    progress?: CSSProperties | ViewStyle;
    midrolls?: CSSProperties | ViewStyle;
    progressDot?: CSSProperties | ViewStyle;
    progressBar?: CSSProperties | ViewStyle;
    progressLive?: CSSProperties | ViewStyle;
    progressTime?: IProgressTimeStyles;
    thumbnail?: IThumbnailStyles;
    midrollsContainer?: CSSProperties | ViewStyle;
}

export interface ITimeline extends ComponentVariant {
    playerInstance: PlayerBase;
    programRange?: IProgramRange | null;
    className?: string;
    styles?: ITimelineStyles;
    fromBottomToUpRef?: Ref<any>;
    dvrWindowOverride?: TimeInMs;
    assetType?: ASSET_TYPE;
    shouldShowBufferedRanges?: boolean;
    shouldShowProgressTime?: boolean;
    showHours?: boolean | 'auto';
    shouldShowThumbnail?: boolean;
}

export interface IPosition {
    x: number;
    y: number;
}

export enum PLAYER_UI_TEST_IDS {
    SEEK_NEGATIVE_BUTTON = 'player_minus_time_button',
    SEEK_POSITIVE_BUTTON = 'player_plus_time_button',
    BACK_BUTTON = 'player_back_button',
    HEADER_TITLE = 'player_title',
    PAUSE_BUTTON = 'pause_button',
    PLAY_BUTTON = 'player_play',
    CURRENT_TIME = 'player_current_time',
    TIMELINE_SEEKER = 'player_seeker',
    TIMELINE_SEEKER_POINT = 'player_seeker_point',
    PLAYER_ALL_CHANNELS_BUTTON = 'player_all_channels_button',
    PLAYER_RESTART_BUTTON = 'player_restart_button',
    PLAYER_GOTOLIVE_BUTTON = 'player_gotolive_button',
    PLAYER_ALL_EPISODES_BUTTON = 'player_all_episodes_button',
    PLAYER_RELATED_BUTTON = 'player_related_button',
    PLAYER_RECORD_BUTTON = 'player_record_button',
    PLAYER_AUDIO_SUBTITLES_BUTTON = 'player_audio_subtitles_button',
    PLAYER_QUALITY_BUTTON = 'player_quality_button',
    PLAYER_TOTAL_TIME = 'player_total_time',
    PLAYER_LIVE_TIME = 'player_live_time',
    PREVIOUS_CHANNEL_BUTTON = 'previous_channel_button',
    NEXT_CHANNEL_BUTTON = 'next_channel_button',
}
